import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import { useKeyboardState } from "@swiggy-private/checkout-core";
import { isEmpty, useEffectOnce } from "@swiggy-private/core-ui";
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { IRightActionDetails, RightActionTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";

import { OrderTypePreferenceOptions } from "../order-type-preference-options";
import { ORDER_PREFERENCE, ORDER_TYPE_BOTTOM_SHEET_COPY } from "../order-type-preference-options/constants";
import { ON_PREMISE_MOBILE_TEXT_FIELD_DETAILS } from "./constants";
import { clampOnPremiseMobileInput, clampOnPremiseScreenSeatInput, ON_PREMISE_MOBILE_MAX_DIGITS } from "./helpers";
import {
    Divider,
    FooterBar,
    getOnPremiseTextFieldContainerStyle,
    HeaderBlock,
    MobileBlock,
    ScrollBody,
    InputWrapper,
    SheetOuter,
    SubtitleText,
    TitleText,
} from "./OrderTypeBottomSheet.style";
import type { OrderTypeBottomSheetProps } from "./types";

const OrderTypeBottomSheet: React.FC<OrderTypeBottomSheetProps> = ({
    isOpen,
    currentOrderPreference,
    currentAudiNumber,
    currentSeatNumber,
    currentMobileNumber,
    updateCartInProgress,
    openFocusTarget,
    analytics,
    onClose,
    onConfirmDeliverToSeat,
    onConfirmPickupAtCounter,
}) => {
    const [preference, setPreference] = useState<string>(ORDER_PREFERENCE.DELIVER_TO_SEAT);
    const [audiNumber, setAudiNumber] = useState("");
    const [seatNumber, setSeatNumber] = useState("");
    const [mobileNumber, setMobileNumber] = useState("");
    const screenInputRef = useRef<HTMLInputElement>(null);
    const mobileInputRef = useRef<HTMLInputElement>(null);
    const scrollBodyRef = useRef<HTMLDivElement>(null);
    const hasTrackedMobileFocusRef = useRef(false);
    const isEditFlowRef = useRef(false);
    const openFocusTimeoutRef = useRef<number>();

    useKeyboardState(scrollBodyRef);

    useEffectOnce(() => {
        isEditFlowRef.current = !isEmpty(currentOrderPreference);
        analytics.onImpression();
    });

    useEffect(() => {
        if (!isOpen) return;
        setPreference(
            currentOrderPreference === ORDER_PREFERENCE.PICKUP_AT_COUNTER
                ? ORDER_PREFERENCE.PICKUP_AT_COUNTER
                : ORDER_PREFERENCE.DELIVER_TO_SEAT,
        );
        setAudiNumber(clampOnPremiseScreenSeatInput(currentAudiNumber));
        setSeatNumber(clampOnPremiseScreenSeatInput(currentSeatNumber));
        setMobileNumber(clampOnPremiseMobileInput(currentMobileNumber));
        hasTrackedMobileFocusRef.current = false;
    }, [isOpen, currentOrderPreference, currentAudiNumber, currentSeatNumber, currentMobileNumber]);

    const handleMobileFocus = useCallback(() => {
        if (hasTrackedMobileFocusRef.current) return;
        hasTrackedMobileFocusRef.current = true;
        analytics.onMobileNumberFocus();
    }, [analytics]);

    const clearOpenFocusTimeout = useCallback(() => {
        if (openFocusTimeoutRef.current !== undefined) {
            window.clearTimeout(openFocusTimeoutRef.current);
            openFocusTimeoutRef.current = undefined;
        }
    }, []);

    useEffect(() => {
        if (!isOpen || !openFocusTarget) {
            clearOpenFocusTimeout();
            return;
        }

        openFocusTimeoutRef.current = window.setTimeout(() => {
            openFocusTimeoutRef.current = undefined;
            if (openFocusTarget === "mobile") {
                mobileInputRef.current?.focus();
                handleMobileFocus();
                return;
            }
            if (preference === ORDER_PREFERENCE.DELIVER_TO_SEAT) {
                screenInputRef.current?.focus();
            } else {
                mobileInputRef.current?.focus();
            }
        }, 0);

        return clearOpenFocusTimeout;
    }, [isOpen, openFocusTarget, preference, handleMobileFocus, clearOpenFocusTimeout]);

    const handleAudiChange = useCallback((value: string) => {
        setAudiNumber(clampOnPremiseScreenSeatInput(value));
    }, []);

    const handleSeatChange = useCallback((value: string) => {
        setSeatNumber(clampOnPremiseScreenSeatInput(value));
    }, []);

    const handleMobileChange = useCallback((value: string) => {
        setMobileNumber(clampOnPremiseMobileInput(value));
    }, []);

    const handleMobileClear = useCallback(() => {
        setMobileNumber("");
    }, []);

    const handlePreferenceChange = useCallback(
        (option: string) => {
            setPreference(option);
            if (option === ORDER_PREFERENCE.DELIVER_TO_SEAT) {
                analytics.onDeliverPreferenceClick();
            } else {
                analytics.onPickupPreferenceClick();
            }
        },
        [analytics],
    );

    const mobileDigits = useMemo(() => clampOnPremiseMobileInput(mobileNumber), [mobileNumber]);

    const isConfirmDisabled = useMemo(() => {
        if (updateCartInProgress) return true;
        if (mobileDigits.length !== ON_PREMISE_MOBILE_MAX_DIGITS) return true;
        if (preference === ORDER_PREFERENCE.DELIVER_TO_SEAT) {
            return !audiNumber.trim() || !seatNumber.trim();
        }
        return false;
    }, [updateCartInProgress, mobileDigits, preference, audiNumber, seatNumber]);

    const handleConfirm = useCallback(() => {
        if (isConfirmDisabled) return;
        if (preference === ORDER_PREFERENCE.DELIVER_TO_SEAT) {
            const trimmedAudi = audiNumber.trim();
            const trimmedSeat = seatNumber.trim();
            analytics.onConfirm(ORDER_PREFERENCE.DELIVER_TO_SEAT, trimmedSeat, trimmedAudi, isEditFlowRef.current);
            onConfirmDeliverToSeat(trimmedAudi, trimmedSeat, mobileDigits);
        } else {
            analytics.onConfirm(ORDER_PREFERENCE.PICKUP_AT_COUNTER, "", "", isEditFlowRef.current);
            onConfirmPickupAtCounter(mobileDigits);
        }
    }, [
        isConfirmDisabled,
        preference,
        audiNumber,
        seatNumber,
        mobileDigits,
        analytics,
        onConfirmDeliverToSeat,
        onConfirmPickupAtCounter,
    ]);

    const mobileRightActions = useMemo((): [IRightActionDetails] | undefined => {
        if (!mobileDigits.length) return undefined;
        return [{ type: RightActionTypes.CLEAR, onClick: handleMobileClear }];
    }, [mobileDigits.length, handleMobileClear]);

    const theme = useTheme();
    const textFieldContainer = useMemo(() => getOnPremiseTextFieldContainerStyle(theme), [theme]);

    const mobileTextFieldDetails = useMemo(
        () => ({
            ...ON_PREMISE_MOBILE_TEXT_FIELD_DETAILS,
            value: mobileDigits,
            onChange: handleMobileChange,
            inputStyle: textFieldContainer,
            attrs: {
                ...ON_PREMISE_MOBILE_TEXT_FIELD_DETAILS.attrs,
                onFocus: handleMobileFocus,
            },
        }),
        [mobileDigits, handleMobileChange, textFieldContainer, handleMobileFocus],
    );

    if (!isOpen) return null;

    return (
        <HalfCard isOpen closeOnOverlayClick onClose={onClose} variant="v3" hasCustomPadding>
            <SheetOuter>
                <ScrollBody ref={scrollBodyRef}>
                    <InputWrapper>
                        <HeaderBlock>
                            <TitleText variant="V2.subheading1.neutral.bold" color="text_Highest_Emphasis">
                                {ORDER_TYPE_BOTTOM_SHEET_COPY.TITLE}
                            </TitleText>
                            <SubtitleText variant="V2.body2.neutral.medium" color="text_Med_Emphasis">
                                {ORDER_TYPE_BOTTOM_SHEET_COPY.SUBTITLE}
                            </SubtitleText>
                        </HeaderBlock>

                        <OrderTypePreferenceOptions
                            preference={preference}
                            audiNumber={audiNumber}
                            seatNumber={seatNumber}
                            screenInputRef={screenInputRef}
                            onPreferenceChange={handlePreferenceChange}
                            onAudiChange={handleAudiChange}
                            onSeatChange={handleSeatChange}
                        />

                        <Divider />

                        <MobileBlock>
                            <TextField
                                ref={mobileInputRef}
                                textFieldDetails={mobileTextFieldDetails}
                                rightActionDetails={mobileRightActions}
                            />
                        </MobileBlock>
                    </InputWrapper>

                    <FooterBar>
                        <SuperButton
                            variant="fullwidth"
                            buttonType="primary"
                            buttonSize="large"
                            isDisabled={isConfirmDisabled}
                            onClick={handleConfirm}
                        >
                            {ORDER_TYPE_BOTTOM_SHEET_COPY.CONFIRM}
                        </SuperButton>
                    </FooterBar>
                </ScrollBody>
            </SheetOuter>
        </HalfCard>
    );
};

export default React.memo(OrderTypeBottomSheet);
